import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AsyncSelect from 'react-select/async';
import { rentalService, userService, rentableItemService } from '../../services/api';
import '../../styles/common.css';

const RentalForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    userId: '',
    rentableItemId: '',
    quantity: 1,
    startDate: new Date().toISOString().split('T')[0],
    expectedReturnDate: '',
    notes: ''
  });

  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  const loadRental = useCallback(async () => {
    try {
      setLoading(true);
      const result = await rentalService.getById(id);
      if (result.success) {
        const rental = result.data;
        setFormData({
          userId: rental.userId,
          rentableItemId: rental.rentableItemId,
          quantity: rental.quantity,
          startDate: rental.startDate ? rental.startDate.split('T')[0] : '',
          expectedReturnDate: rental.expectedReturnDate ? rental.expectedReturnDate.split('T')[0] : '',
          notes: rental.notes || ''
        });

        // Set selected options
        setSelectedUser({
          value: rental.user.id,
          label: `${rental.user.firstName} ${rental.user.lastName} (${rental.user.email})`
        });

        setSelectedItem({
          value: rental.rentableItem.id,
          label: rental.rentableItem.name
        });
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('Errore nel caricamento del noleggio');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (isEdit) {
      loadRental();
    }
  }, [isEdit, loadRental]);

  // Load users for select
  const loadUsersAsync = useCallback((inputValue) => {
    return new Promise((resolve) => {
      userService.getUsers({ search: inputValue, page: 1, limit: 20 }).then(response => {
        if (response.success) {
          const formattedUsers = (response.data.items || []).map(user => ({
            value: user.id,
            label: `${user.firstName} ${user.lastName} (${user.email})`
          }));
          resolve(formattedUsers);
        } else {
          resolve([]);
        }
      }).catch(() => {
        resolve([]);
      });
    });
  }, []);

  // Load rentable items for select
  const loadItemsAsync = useCallback((inputValue) => {
    return new Promise((resolve) => {
      rentableItemService.getAll(1, 20, inputValue, true).then(response => {
        if (response.success) {
          const formattedItems = (response.data.items || []).map(item => ({
            value: item.id,
            label: `${item.name} - ${new Intl.NumberFormat('it-IT', {
              style: 'currency',
              currency: 'EUR'
            }).format(item.price)}`
          }));
          resolve(formattedItems);
        } else {
          resolve([]);
        }
      }).catch(() => {
        resolve([]);
      });
    });
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors({
        ...validationErrors,
        [name]: ''
      });
    }
  };

  const handleUserChange = (option) => {
    setSelectedUser(option);
    setFormData({
      ...formData,
      userId: option ? option.value : ''
    });
    if (validationErrors.userId) {
      setValidationErrors({
        ...validationErrors,
        userId: ''
      });
    }
  };

  const handleItemChange = (option) => {
    setSelectedItem(option);
    setFormData({
      ...formData,
      rentableItemId: option ? option.value : ''
    });
    if (validationErrors.rentableItemId) {
      setValidationErrors({
        ...validationErrors,
        rentableItemId: ''
      });
    }
  };

  const validate = () => {
    const errors = {};

    if (!formData.userId) {
      errors.userId = 'Seleziona un utente';
    }

    if (!formData.rentableItemId) {
      errors.rentableItemId = 'Seleziona un oggetto';
    }

    if (!formData.quantity || formData.quantity < 1) {
      errors.quantity = 'La quantità deve essere almeno 1';
    }

    if (!formData.startDate) {
      errors.startDate = 'La data di inizio è obbligatoria';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      const result = isEdit
        ? await rentalService.update(id, formData)
        : await rentalService.create(formData);

      if (result.success) {
        navigate('/rentals');
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('Errore nel salvataggio del noleggio');
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEdit) {
    return (
      <div className="container-fluid">
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Caricamento...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12 col-lg-8 mx-auto">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h1>{isEdit ? 'Modifica Noleggio' : 'Nuovo Noleggio'}</h1>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => navigate('/rentals')}
            >
              <i className="fas fa-arrow-left me-2"></i>
              Torna alla lista
            </button>
          </div>

          {error && (
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              {error}
              <button
                type="button"
                className="btn-close"
                onClick={() => setError('')}
                aria-label="Close"
              ></button>
            </div>
          )}

          <div className="card">
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-12 mb-3">
                    <label htmlFor="userId" className="form-label">
                      Utente <span className="text-danger">*</span>
                    </label>
                    <AsyncSelect
                      id="userId"
                      cacheOptions
                      loadOptions={loadUsersAsync}
                      onChange={handleUserChange}
                      value={selectedUser}
                      placeholder="Cerca e seleziona un utente..."
                      noOptionsMessage={() => "Nessun utente trovato"}
                      loadingMessage={() => "Caricamento utenti..."}
                      isDisabled={isEdit}
                      className={validationErrors.userId ? 'is-invalid' : ''}
                    />
                    {validationErrors.userId && (
                      <div className="text-danger small mt-1">{validationErrors.userId}</div>
                    )}
                  </div>

                  <div className="col-md-12 mb-3">
                    <label htmlFor="rentableItemId" className="form-label">
                      Oggetto da noleggiare <span className="text-danger">*</span>
                    </label>
                    <AsyncSelect
                      id="rentableItemId"
                      cacheOptions
                      defaultOptions
                      loadOptions={loadItemsAsync}
                      onChange={handleItemChange}
                      value={selectedItem}
                      placeholder="Cerca e seleziona un oggetto..."
                      noOptionsMessage={() => "Nessun oggetto trovato"}
                      loadingMessage={() => "Caricamento oggetti..."}
                      isDisabled={isEdit}
                      className={validationErrors.rentableItemId ? 'is-invalid' : ''}
                    />
                    {validationErrors.rentableItemId && (
                      <div className="text-danger small mt-1">{validationErrors.rentableItemId}</div>
                    )}
                    <small className="form-text text-muted">
                      Sono mostrati solo gli oggetti attivi
                    </small>
                  </div>

                  <div className="col-md-4 mb-3">
                    <label htmlFor="quantity" className="form-label">
                      Quantità <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      className={`form-control ${validationErrors.quantity ? 'is-invalid' : ''}`}
                      id="quantity"
                      name="quantity"
                      value={formData.quantity}
                      onChange={handleChange}
                      min="1"
                      required
                    />
                    {validationErrors.quantity && (
                      <div className="invalid-feedback">{validationErrors.quantity}</div>
                    )}
                  </div>

                  <div className="col-md-4 mb-3">
                    <label htmlFor="startDate" className="form-label">
                      Data Inizio <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      className={`form-control ${validationErrors.startDate ? 'is-invalid' : ''}`}
                      id="startDate"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleChange}
                      required
                    />
                    {validationErrors.startDate && (
                      <div className="invalid-feedback">{validationErrors.startDate}</div>
                    )}
                  </div>

                  <div className="col-md-4 mb-3">
                    <label htmlFor="expectedReturnDate" className="form-label">
                      Data Restituzione Prevista
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      id="expectedReturnDate"
                      name="expectedReturnDate"
                      value={formData.expectedReturnDate}
                      onChange={handleChange}
                      min={formData.startDate}
                    />
                    <small className="form-text text-muted">Opzionale</small>
                  </div>

                  <div className="col-md-12 mb-4">
                    <label htmlFor="notes" className="form-label">
                      Note
                    </label>
                    <textarea
                      className="form-control"
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      rows="3"
                      placeholder="Note aggiuntive sul noleggio..."
                    ></textarea>
                  </div>
                </div>

                <div className="d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => navigate('/rentals')}
                    disabled={loading}
                  >
                    Annulla
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Salvataggio...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save me-2"></i>
                        {isEdit ? 'Aggiorna' : 'Crea Noleggio'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RentalForm;
